import * as state from './state.js';
import { updatePreviewStatus } from './preview-status.js';
import { decompressYaml } from './share-link.js';
import { getDoc, setDoc } from './editor-view.js';

export function normalizeExamplePath(path) {
    const prefix = 'playground/charts/';
    if (path.startsWith(prefix)) {
        return path.slice(prefix.length);
    }
    return path;
}

export function formatCategoryName(category) {
    if (category === 'root') return 'General';
    return category
        .split(/[-_]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

export function createExampleCard(example, selectExampleFromGalleryFn) {
    const card = document.createElement('div');
    card.className = 'example-card';
    card.dataset.path = example.path;

    const img = document.createElement('img');
    img.className = 'example-thumbnail';
    img.loading = 'lazy';
    img.alt = `${example.name} preview`;
    img.src = state.withProject(`/api/thumbnails/${example.body_hash}.svg`);
    card.appendChild(img);

    const title = document.createElement('div');
    title.className = 'example-title';
    title.textContent = example.name;

    card.appendChild(title);

    card.addEventListener('click', () => {
        selectExampleFromGalleryFn(example);
    });

    return card;
}

export function buildExamplesGallery(byCategory, selectExampleFromGalleryFn) {
    const gallery = document.getElementById('examples-gallery');
    gallery.innerHTML = '';

    const categoryOrder = ['general', 'boards', 'charts', 'cards', 'variables', 'reference', 'tables-and-kpis', 'labs', 'archive', 'proto-docs', 'specimens', 'errors'];

    const sortedCategories = Object.keys(byCategory).sort((a, b) => {
        const aIndex = categoryOrder.indexOf(a.toLowerCase());
        const bIndex = categoryOrder.indexOf(b.toLowerCase());
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return a.localeCompare(b);
    });

    sortedCategories.forEach(category => {
        const categorySection = document.createElement('div');
        categorySection.className = 'examples-category';

        const storageKey = `gallery-collapsed-${category}`;
        const isCollapsed = localStorage.getItem(storageKey) === 'true';
        if (isCollapsed) {
            categorySection.setAttribute('data-collapsed', '');
        }

        const categoryTitle = document.createElement('h3');
        categoryTitle.className = 'examples-category-title';
        categoryTitle.setAttribute('role', 'button');
        categoryTitle.setAttribute('tabindex', '0');
        categoryTitle.setAttribute('aria-expanded', isCollapsed ? 'false' : 'true');

        const chevron = document.createElement('span');
        chevron.className = 'examples-category-chevron';
        chevron.setAttribute('aria-hidden', 'true');
        categoryTitle.appendChild(chevron);
        categoryTitle.appendChild(document.createTextNode(formatCategoryName(category)));

        categorySection.appendChild(categoryTitle);

        const grid = document.createElement('div');
        grid.className = 'examples-grid';
        if (isCollapsed) grid.style.maxHeight = '0';

        let expandHandler = null;
        let expandTimeout = null;
        const clearExpandState = () => {
            grid.style.maxHeight = '';
            grid.style.overflow = '';
            if (expandHandler) {
                grid.removeEventListener('transitionend', expandHandler);
                expandHandler = null;
            }
            clearTimeout(expandTimeout);
            expandTimeout = null;
        };
        const toggleCollapse = () => {
            const collapsed = categorySection.hasAttribute('data-collapsed');
            if (collapsed) {
                categorySection.removeAttribute('data-collapsed');
                categoryTitle.setAttribute('aria-expanded', 'true');
                grid.style.overflow = 'hidden';
                grid.style.maxHeight = grid.scrollHeight + 'px';
                if (expandHandler) grid.removeEventListener('transitionend', expandHandler);
                clearTimeout(expandTimeout);
                expandHandler = (e) => {
                    if (e.propertyName !== 'max-height') return;
                    clearExpandState();
                };
                grid.addEventListener('transitionend', expandHandler);
                expandTimeout = setTimeout(clearExpandState, 300);
                localStorage.removeItem(storageKey);
            } else {
                clearExpandState();
                grid.style.maxHeight = grid.scrollHeight + 'px';
                void grid.offsetHeight;
                categorySection.setAttribute('data-collapsed', '');
                categoryTitle.setAttribute('aria-expanded', 'false');
                grid.style.maxHeight = '0';
                localStorage.setItem(storageKey, 'true');
            }
        };

        categoryTitle.addEventListener('click', toggleCollapse);
        categoryTitle.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleCollapse();
            }
        });

        byCategory[category].forEach(example => {
            const card = createExampleCard(example, selectExampleFromGalleryFn);
            grid.appendChild(card);
        });

        categorySection.appendChild(grid);
        gallery.appendChild(categorySection);
    });
}

export function showInlineExamplesGallery() {
    const previewContent = document.getElementById('preview-content');
    if (!state.examplesData || state.examplesData.length === 0) {
        previewContent.innerHTML = '<div class="preview-placeholder">Loading examples...</div>';
        return;
    }

    // Idempotent: if the gallery is already mounted, leave it alone. Rebuilding
    // would re-fetch every visible thumbnail.
    if (previewContent.querySelector('.inline-examples-gallery')) {
        updatePreviewStatus('Examples', 'ready');
        return;
    }

    const container = document.createElement('div');
    container.className = 'inline-examples-gallery';

    const byCategory = {};
    state.examplesData.forEach(ex => {
        if (!byCategory[ex.category]) byCategory[ex.category] = [];
        byCategory[ex.category].push(ex);
    });

    const categoryOrder = ['general', 'boards', 'charts', 'cards', 'variables', 'reference', 'tables-and-kpis', 'labs', 'archive', 'proto-docs', 'specimens', 'errors'];
    const sortedCategories = Object.keys(byCategory).sort((a, b) => {
        const aIndex = categoryOrder.indexOf(a.toLowerCase());
        const bIndex = categoryOrder.indexOf(b.toLowerCase());
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return a.localeCompare(b);
    });

    sortedCategories.forEach((category, index) => {
        const section = document.createElement('div');
        section.className = 'examples-category';

        const title = document.createElement('h3');
        title.className = 'examples-category-title inline-category-title';
        title.textContent = formatCategoryName(category);

        section.appendChild(title);

        const grid = document.createElement('div');
        grid.className = 'examples-grid';

        byCategory[category].forEach(example => {
            const card = document.createElement('div');
            card.className = 'example-card';
            card.dataset.path = example.path;

            const img = document.createElement('img');
            img.className = 'example-thumbnail';
            img.loading = 'lazy';
            img.alt = `${example.name} preview`;
            img.src = state.withProject(`/api/thumbnails/${example.body_hash}.svg`);
            card.appendChild(img);

            const name = document.createElement('div');
            name.className = 'example-title';
            name.textContent = example.name;
            card.appendChild(name);

            card.addEventListener('click', () => {
                const selector = document.getElementById('example-selector');
                selector.value = example.path;
                selector.dispatchEvent(new Event('change'));
            });

            grid.appendChild(card);
        });

        section.appendChild(grid);
        container.appendChild(section);
    });

    previewContent.innerHTML = '';
    previewContent.appendChild(container);

    updatePreviewStatus('Examples', 'ready');
}

export function selectExampleFromGallery(example, closeExamplesModalFn) {
    closeExamplesModalFn();

    const selector = document.getElementById('example-selector');
    selector.value = example.path;

    // The 'change' handler updates the URL synchronously before it awaits the
    // YAML fetch, so no separate updateUrlParams call is needed here.
    selector.dispatchEvent(new Event('change'));
}

export function openExamplesModal() {
    const modal = document.getElementById('examples-modal');
    modal.showModal();

    const closeBtn = document.getElementById('examples-modal-close');
    if (closeBtn) {
        closeBtn.focus();
    }
}

export function closeExamplesModal() {
    const modal = document.getElementById('examples-modal');
    modal.close();
}

function rebuildSelectorAndGallery(examples) {
    const selector = document.getElementById('example-selector');
    const byCategory = {};

    examples.forEach(ex => {
        if (!byCategory[ex.category]) byCategory[ex.category] = [];
        byCategory[ex.category].push(ex);
    });

    // Rebuild the hidden select so option paths match the gallery cards.
    // Without this, clicking a card sets selector.value to a path with no
    // matching <option>, the change handler receives an empty value, and
    // the YAML loader never fires.
    if (selector) {
        selector.innerHTML = '';
        Object.keys(byCategory).sort().forEach(category => {
            const optgroup = document.createElement('optgroup');
            optgroup.label = category === 'root' ? 'Root' : category.charAt(0).toUpperCase() + category.slice(1);
            byCategory[category].forEach(ex => {
                const option = document.createElement('option');
                option.value = ex.path;
                option.textContent = ex.name;
                optgroup.appendChild(option);
            });
            selector.appendChild(optgroup);
        });
    }

    if (!state.gallerySelectFn) {
        console.error('rebuildSelectorAndGallery: gallerySelectFn not set — call loadExamples first');
        return;
    }
    buildExamplesGallery(byCategory, state.gallerySelectFn);
}

export function initExamplesModal() {
    const openBtn = document.getElementById('examples-btn');
    const closeBtn = document.getElementById('examples-modal-close');
    const modal = document.getElementById('examples-modal');

    if (openBtn) {
        openBtn.addEventListener('click', openExamplesModal);
    }

    if (closeBtn) {
        closeBtn.addEventListener('click', closeExamplesModal);
    }

    if (modal) {
        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                closeExamplesModal();
            }
        });
    }
}

export async function loadPlaygroundConfig() {
    try {
        const response = await fetch(state.withProject('/api/config'));
        if (response.ok) {
            const config = await response.json();
            state.setPlaygroundConfig(config);
        }
    } catch (error) {
        console.error('Error loading config:', error);
    }
}

// True while a 'change' dispatch on the example-selector is programmatic
// (initial deep-link load or a popstate-driven reload) rather than a user
// click/selection. The change handler reads this synchronously — before its
// first await — to decide whether selecting the example should push a new
// history entry or just replace the current one.
let applyingHistoryState = false;

// Captured in initGallery so handlePopState can trigger a preview refresh
// after restoring a ?y= share-link entry — same mechanism as the example
// change handler's explicit sendYAMLToPreviewFn() call.
let sendYAMLFn = null;

// Applies the ?example= param (if any and not overridden by ?y=) from the
// given URLSearchParams: sets the selector value and dispatches 'change'.
// Shared by loadExamples (initial load) and handlePopState (Back/Forward) so
// the two deep-link-apply paths don't diverge.
function applyExampleFromParams(params, examples) {
    // Guard: examples may be undefined/null if loadExamples hasn't run yet
    // (popstate can fire before the async fetch resolves or after a failure).
    if (!Array.isArray(examples) || examples.length === 0) {
        return false;
    }

    const selector = document.getElementById('example-selector');
    const compressedYamlParam = params.get('y');
    if (compressedYamlParam) {
        return false;
    }

    const exampleParam = params.get('example');
    if (!exampleParam) {
        return false;
    }

    const normalizedExample = normalizeExamplePath(exampleParam);
    const example = examples.find(ex => ex.path === normalizedExample);
    if (!example) {
        return false;
    }

    applyingHistoryState = true;
    selector.value = normalizedExample;
    selector.dispatchEvent(new Event('change'));
    applyingHistoryState = false;
    return true;
}

// Populate + reveal the project switcher when the server serves >1 project.
// Options are the server-supplied project names; the selected value is the
// current ?project= index (default 0). Switching navigates to /?project=<i>,
// a full reload — the new project has its own gallery, sources, and examples.
export function initProjectSelector() {
    const selector = document.getElementById('project-selector');
    if (!selector) return;

    const projects = state.playgroundConfig.projects || [];
    if (projects.length <= 1) {
        selector.style.display = 'none';
        return;
    }

    selector.innerHTML = '';
    projects.forEach((name, index) => {
        const option = document.createElement('option');
        option.value = String(index);
        option.textContent = `Project: ${name}`;
        selector.appendChild(option);
    });
    selector.value = state.activeProject() || '0';
    selector.style.display = '';

    selector.addEventListener('change', (e) => {
        window.location.href = `/?project=${encodeURIComponent(e.target.value)}`;
    });
}

export async function loadExamples() {
    try {
        initProjectSelector();
        const response = await fetch(state.withProject('/api/examples'));
        if (!response.ok) {
            console.error(`Failed to load examples: ${response.status} ${response.statusText}`);
            return;
        }
        const examples = await response.json();
        state.setExamplesData(examples);

        const selectFn = (example) => selectExampleFromGallery(example, closeExamplesModal);
        state.setGallerySelectFn(selectFn);
        rebuildSelectorAndGallery(examples);

        if (getDoc(state.editor).trim()) {
            // The editor already holds a board (a ?y= share link decoded at
            // mount, or one typed while this fetch was in flight) and owns the
            // preview. Neither a deep-linked example nor the empty-editor
            // gallery may clobber it.
            return;
        }

        const params = new URLSearchParams(window.location.search);
        if (applyExampleFromParams(params, examples)) {
            return;
        }

        showInlineExamplesGallery();
    } catch (error) {
        console.error('Error loading examples:', error);
    }
}

// Applies ?f= and ?vars= from the given URLSearchParams.
// Used by both the initial DOMContentLoaded path in bootstrap.js and by
// handlePopState so the two load paths stay in sync.
// Format is ALWAYS applied (absent ?f= resets to svg) — a conditional set
// would leave the selector on the previous format when pressing Back.
export function applyFormatAndVarsFromParams(params) {
    const formatSelector = document.getElementById('format-selector');
    if (formatSelector) {
        formatSelector.value = params.get('f') || 'svg';
    }

    const varsParam = params.get('vars');
    if (varsParam) {
        try {
            state.setUrlVariables(JSON.parse(varsParam));
        } catch (error) {
            console.error('Error parsing variables from URL:', error);
            state.setUrlVariables(null);
        }
    } else {
        state.setUrlVariables(null);
    }
}

// Handles browser Back/Forward: re-applies the example (+ format/vars, via
// the same URL) that the popped history entry points at. Guarded by
// applyingHistoryState so the resulting 'change' dispatch replaces the URL
// instead of pushing a new entry — otherwise Back/Forward would spam history.
export function handlePopState() {
    const params = new URLSearchParams(window.location.search);
    // Always restore format and vars — both ?y= and ?example= URLs can carry
    // ?f= and ?vars=, so this must run on every popstate path.
    applyFormatAndVarsFromParams(params);
    const compressedYamlParam = params.get('y');
    if (compressedYamlParam) {
        // Restore a compressed-YAML share-link entry. No history push — this
        // is a popstate reaction; the URL is already what the browser navigated to.
        try {
            const yaml = decompressYaml(compressedYamlParam);
            setDoc(state.editor, yaml);
            if (sendYAMLFn) sendYAMLFn();
        } catch (error) {
            console.error('Error decoding compressed YAML:', error);
        }
        return;
    }
    applyExampleFromParams(params, state.examplesData);
}

export function initGallery(sendYAMLToPreviewFn, resetVariablesFn, updateUrlParamsFn) {
    sendYAMLFn = sendYAMLToPreviewFn;
    initExamplesModal();

    document.getElementById('example-selector').addEventListener('change', async (e) => {
        const path = e.target.value;
        if (!path) {
            state.setCurrentExamplePath(null);
            return;
        }
        state.setCurrentExamplePath(path);

        // Apply variable state first (synchronous, no network), then update the
        // URL immediately so the address bar reflects the click — including any
        // deep-linked ?vars= — before the (possibly slow) YAML fetch resolves.
        // Selecting an example is conceptually a link; don't make the URL wait
        // on the network. updateUrlParams must run AFTER variables are applied
        // or it would serialize stale/empty vars into the URL.
        resetVariablesFn();
        if (state.urlVariables) {
            state.setCurrentVariables(state.urlVariables);
            state.setUrlVariables(null);
        }
        // A user-initiated selection pushes a new history entry so Back steps
        // to the previous example. A programmatic selection (initial deep-link
        // load or a popstate-driven reload) must not — it would either double
        // up the entry the browser just navigated to, or spam history on load.
        updateUrlParamsFn({ push: !applyingHistoryState });

        try {
            const response = await fetch(state.withProject(`/api/examples/${path}`));
            const data = await response.json();

            setDoc(state.editor, data.yaml);
            state.editor.focus();
            state.setShouldResetScrollOnNextUpdate(true);

            sendYAMLToPreviewFn();
        } catch (error) {
            alert(`Error loading example: ${error.message}`);
        }
    });

    window.addEventListener('popstate', handlePopState);
}
